import { Box, Card, CardActionArea, Typography, useTheme } from '@mui/material';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { PricebookBundle } from '../../../models/PricebookData';
import { PaidByEnum } from '../../../models/LicenseData';
import {
  getActivePrice,
  getIntervalLabel,
  getPriceAmountLabel,
} from '../../../helpers/pricebookUtils';
import PricebookCardTags from './PricebookCardTags';
import { Svgs } from '../../../assets/svg';

interface PricebookRestrictionCardProps {
  pricebookBundle: PricebookBundle;
  addOnPricebook?: PricebookBundle;
  selected: boolean;
  readOnly: boolean;
  inUse: boolean;
  onToggle: () => void;
}

const CHECKMARK_SLOT_SIZE = '20px';
const IN_USE_TOOLTIP = 'This pricebook is currently used by an active license agreement.';

const PricebookRestrictionCard: React.FC<PricebookRestrictionCardProps> = ({
  pricebookBundle,
  addOnPricebook,
  selected,
  readOnly,
  inUse,
  onToggle,
}) => {
  const theme = useTheme();
  const { pricebook, prices } = pricebookBundle;
  const isOrgPays = pricebook.paidBy === PaidByEnum.Org;

  const activePrice = getActivePrice(prices);
  const formattedAmount = getPriceAmountLabel(activePrice);
  const intervalSuffix = getIntervalLabel(activePrice);

  const cardVariant = isOrgPays ? 'pricebookAlternate' : 'pricebookPrimary';
  const isChecked = selected || readOnly;
  const selectionClassName = `${isChecked ? 'pricebook-selected' : 'pricebook-unselected'}${readOnly ? ' pricebook-disabled' : ''}`;

  const CheckmarkIcon = readOnly
    ? Svgs.IconCheckmarkFilledGray
    : isOrgPays
      ? Svgs.IconCheckmarkFilledAlternate
      : Svgs.IconCheckmarkFilledPrimary;

  return (
    <Card variant={cardVariant} className={selectionClassName} sx={{ height: '100%' }}>
      <CardActionArea
        className='pricebook-card-action'
        onClick={readOnly ? undefined : onToggle}
        disableRipple={readOnly}
        sx={readOnly ? { cursor: 'default' } : undefined}>
        <Box display='flex' flexDirection='column' gap='8px' height='100%'>
          <Box display='flex' alignItems='center' gap={1}>
            <Tippy
              content={
                <Typography variant='p16Light' color='secondary.dark'>
                  {pricebook.pricebookName}
                </Typography>
              }
              placement='top'
              arrow
              theme='tippy-rounded'
              onShow={instance => {
                if (instance.reference.scrollWidth <= instance.reference.clientWidth) return false;
              }}>
              <Typography
                variant='p16SemiBold'
                color='secondary.dark'
                className='truncate-one-line'
                sx={{ minWidth: 0 }}>
                {pricebook.pricebookName}
              </Typography>
            </Tippy>
            {inUse && (
              <Tippy
                content={
                  <Typography variant='p16Light' color='secondary.dark'>
                    {IN_USE_TOOLTIP}
                  </Typography>
                }
                placement='top'
                arrow
                theme='tippy-rounded'
                trigger='mouseenter focus'>
                <Box
                  role='button'
                  tabIndex={0}
                  aria-label={`In use. ${IN_USE_TOOLTIP}`}
                  display='flex'
                  alignItems='center'
                  flexShrink={0}
                  sx={{
                    'backgroundColor': theme.palette.tertiary?.disabled,
                    'borderRadius': '10px',
                    'px': 1,
                    'py': 0.5,
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: '1px',
                    },
                  }}>
                  <Typography
                    variant='p12'
                    sx={{
                      color: theme.palette.tertiary?.active,
                      whiteSpace: 'nowrap',
                      lineHeight: 1,
                    }}>
                    In use
                  </Typography>
                </Box>
              </Tippy>
            )}
            <Box width={CHECKMARK_SLOT_SIZE} height={CHECKMARK_SLOT_SIZE} flexShrink={0} ml='auto'>
              {isChecked && <CheckmarkIcon />}
            </Box>
          </Box>

          <Tippy
            content={
              <Typography variant='p16Light' color='secondary.dark'>
                {pricebook.internalAdminDescription}
              </Typography>
            }
            placement='top'
            arrow
            theme='tippy-rounded'
            onShow={instance => {
              if (instance.reference.scrollHeight <= instance.reference.clientHeight) return false;
            }}>
            <Typography variant='p12' color='info.main' className='truncate-two-lines'>
              {pricebook.internalAdminDescription}
            </Typography>
          </Tippy>

          <Box display='flex' flexDirection='column' gap='10px' mt='auto' pt='5px'>
            <Typography component='span' variant='p20SemiBold' color='secondary.dark'>
              <Typography component='span' variant='p20SemiBold' color='info.main'>
                $
              </Typography>
              {formattedAmount}
              {intervalSuffix && (
                <Typography component='span' variant='p16' color='secondary.main'>
                  {intervalSuffix}
                </Typography>
              )}
            </Typography>

            <PricebookCardTags pricebook={pricebook} addOnPricebook={addOnPricebook} />
          </Box>
        </Box>
      </CardActionArea>
    </Card>
  );
};

export default PricebookRestrictionCard;
